import React, { useEffect, useState } from 'react';
import { without } from 'lodash';
import { AddTopicFormModalProps, AddTopicFormModalValues, Segment } from './types';
import style from './style.module.scss';
import cx from 'classnames';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { Modal } from 'react-bootstrap';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';
import { PremiumBadge } from '@magoosh/lib/info_badges';
import { useUserContext } from '@magoosh/context/user_context';

const initialValues: AddTopicFormModalValues = {
  searchValue: ''
};

export const AddTopicFormModal: React.FC<AddTopicFormModalProps> = (props) => {
  const { isOpen, closeModal, onSubmit } = props;
  const { adaptivePrepData } = useUserContext();

  const [matchingSegments, setMatchingSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segments, setSegments] = useState<Segment[]>(props.segments || []);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (segments && segments.length) return;
    fetchSegments();
  });

  const fetchSegments = () => {
    fetch(paths.api.segments()).then((resp) => {
      setSegments(resp.segments);
    });
  };

  const findMatchingSegments = (e) => {
    setSelectedSegment(null);

    const value = e.target.value.toLocaleLowerCase();

    if (value === '') {
      setMatchingSegments([]);
    } else {
      const filteredSegments = segments.filter((segment) => {
        return (
          segment.name.toLocaleLowerCase().includes(value) ||
          segment.subsection.name.toLocaleLowerCase().includes(value)
        );
      });
      setMatchingSegments(filteredSegments);
    }
  };

  const handleOnHide = (form: FormikProps<AddTopicFormModalValues>) => {
    resetForm(form);
    closeModal();
  };

  const handleOnSubmit = (
    values: AddTopicFormModalValues,
    { setFieldError, setSubmitting }: FormikActions<AddTopicFormModalValues>
  ): void => {
    setSubmitting(true);
    onSubmit(selectedSegment.id)
      .then(() => {
        // Remove the segment that was just added from the list of segments so it can't be
        // added again
        setSegments(without(segments, selectedSegment));

        setShowConfirmation(true);
        setSubmitting(false);
      })
      .catch((resp) => {
        setSubmitting(false);
        setFieldError('searchValue', resp.errors._error);
      });
  };

  const resetForm = (form: FormikProps<AddTopicFormModalValues>) => {
    form.setFieldValue('searchValue', '');
    setSelectedSegment(null);
    setMatchingSegments([]);
    setShowConfirmation(false);
  };

  const renderSearch = ({
    isSubmitting,
    setFieldValue,
    submitForm
  }: FormikProps<AddTopicFormModalValues>) => {
    return (
      <>
        <Modal.Header className={cx(style.header, 'u-padding-B-n')} closeButton>
          <span className="d-flex justify-content-between align-items-center u-padding-T-m u-padding-B-xs">
            <h3 className="u-margin-A-n">
              <strong>Add a Topic to Your Study Plan</strong>
            </h3>
            {adaptivePrepData.isTrial && <PremiumBadge className={'u-margin-H-xs'} />}
          </span>
        </Modal.Header>

        <Modal.Body className="u-padding-T-n">
          <p>
            To prioritize a topic that isn’t in your current study plan, simply find and add the topic. Your
            selected topic will appear at the top of your study plan.
          </p>

          <div className={cx(style.listSelector)}>
            <FieldInput
              autoComplete="off"
              label="Select a Topic"
              name="searchValue"
              type="input"
              onKeyUp={(e) => findMatchingSegments(e)}
              placeholder="Enter a topic name to see a list of potential matches..."
            />
            {matchingSegments.length !== 0 && (
              <div className={style.selector}>
                {matchingSegments.map((segment) => {
                  const shouldDisplay =
                    !segment.userSegmentStatus.isCompleted && !segment.userSegmentStatus.isOnCurrentStudyPlan;

                  if (shouldDisplay) {
                    return (
                      <div
                        key={segment.id}
                        className={style.option}
                        onClick={() => {
                          setFieldValue('searchValue', segment.name);
                          setSelectedSegment(segment);
                          setMatchingSegments([]);
                        }}>
                        <p>{segment.name}</p>
                        <small className={style.description}>
                          {segment.subsection.name}, {segment.significanceLevel} Significance
                        </small>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>

          {adaptivePrepData.isTrial ? (
            <SecondaryButton href={paths.plans()} target="_blank" block className="u-margin-T-m">
              Upgrade
            </SecondaryButton>
          ) : (
            <SecondaryButton
              disabled={!selectedSegment}
              submitting={isSubmitting}
              onClick={submitForm}
              block
              className="u-margin-T-m">
              Add Topic
            </SecondaryButton>
          )}

          <div className="text-center u-margin-T-m">
            <a style={{ color: style.grayDarker }} href={paths.topicOverview()}>
              View All Topics
            </a>
          </div>
        </Modal.Body>
      </>
    );
  };

  const renderSuccess = (form: FormikProps<AddTopicFormModalValues>) => {
    return (
      <>
        <Modal.Header className={cx(style.header, 'u-padding-B-n')} closeButton>
          <h3>
            <strong>Success!</strong>
          </h3>
        </Modal.Header>

        <Modal.Body className="u-padding-T-n">
          <p>{selectedSegment.name} has been added to your study plan.</p>

          <div className={style.buttonRow}>
            <SecondaryButton className="u-margin-R-xxs" onClick={() => handleOnHide(form)}>
              View Study Plan
            </SecondaryButton>
            <DefaultButton className="u-margin-L-xxs" onClick={() => resetForm(form)}>
              Add Another Topic
            </DefaultButton>
          </div>
        </Modal.Body>
      </>
    );
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleOnSubmit}>
      {(form) => (
        <Modal className={style.addTopicFormModal} show={isOpen} onHide={() => handleOnHide(form)}>
          {showConfirmation ? renderSuccess(form) : renderSearch(form)}
        </Modal>
      )}
    </Formik>
  );
};

export default AddTopicFormModal;
